import type { JSX, ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { agentTodoMessages } from '../../../../data/projects/timeline/agent-todo/agentTodo.messages';
import { PLAN_COLLAPSE_DISABLED } from '../../../../data/projects/timeline/agent-todo/collapsePlanSteps';
import {
  computePlanProgress,
  isPlanFinished,
} from '../../../../data/projects/timeline/agent-todo/planProgress';
import type { AgentTodoItem } from '../../../../data/projects/timeline/agent-todo/types';
import { useStaggeredTodoItems } from '../../../../data/projects/timeline/agent-todo/useStaggeredTodoItems';
import { PlanCardFrame, type PlanCardPhase } from '../../components/PlanCardFrame';
import { PlanStepsPanel } from './PlanStepsPanel';

export interface PlanCardProps {
  title: string;
  items: readonly AgentTodoItem[];
  /**
   * Tool-call id of the plan's anchor row — becomes the card's DOM id and
   * mounts the top-edge sentinel the pinned summary observes. Omitted on the
   * pre-result card.
   */
  anchorId?: string;
  /** Approval footer slot — rendered inside the card below the steps panel. */
  footer?: ReactNode;
  /**
   * Frozen declined presentation (deny / timed out). A footer always wins so
   * the collapse trigger never coexists with the approval actions.
   */
  declined?: boolean;
  /** The turn ended unsuccessfully, even if its todo updates reached completed. */
  stopped?: boolean;
}

const PROPOSAL_DISCLOSURE_LIMIT = 6;

export const PlanCard = ({
  title,
  items,
  anchorId,
  footer,
  declined,
  stopped,
}: PlanCardProps): JSX.Element => {
  const intl = useIntl();
  // Staggered out here rather than inside the panel because the phase reads
  // it too: the card settles into its finished state only once the last tick
  // has landed, instead of jumping ahead of the strike-through animation.
  const staggeredItems = useStaggeredTodoItems(items);
  const phase: PlanCardPhase = footer
    ? 'awaiting'
    : declined
      ? 'declined'
      : stopped
        ? 'stopped'
        : isPlanFinished(staggeredItems)
          ? 'done'
          : 'running';
  // Same count as the pinned summary, so the two never disagree.
  const { completed, total } = computePlanProgress({
    planTitle: title,
    items: [...staggeredItems],
  });

  return (
    <PlanCardFrame
      title={title}
      phase={phase}
      stepCount={items.length}
      runningStatus={intl.formatMessage(agentTodoMessages.planHeaderProgress, {
        completed,
        total,
      })}
      steps={
        <PlanStepsPanel
          items={staggeredItems}
          // Only a running plan has upcoming work worth keeping in view.
          collapseAfter={phase === 'running' ? undefined : PLAN_COLLAPSE_DISABLED}
          disclosureLimit={phase === 'awaiting' ? PROPOSAL_DISCLOSURE_LIMIT : undefined}
        />
      }
      hasBody={items.length > 0}
      footer={footer}
      resetKey={items.map((item) => `${item.id}:${item.title}`).join('|')}
      anchorId={anchorId}
      testId="plan-card"
    />
  );
};
